/**
 * Vehicle Sighting Types for Twin Cities Rapid Response
 */

import type { GeoLocation } from '../types'

// ============= Vehicle Sighting Types =============

export type VehicleType = 'sedan' | 'suv' | 'van' | 'truck' | 'unmarked' | 'other'
export type VehicleDirection = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'stationary'
export type PlateConfidence = 'full' | 'partial' | 'none'

export const VEHICLE_TYPES: Array<{ key: VehicleType; label: string; emoji: string }> = [
  { key: 'suv', label: 'SUV', emoji: '🚙' },
  { key: 'sedan', label: 'Sedan', emoji: '🚗' },
  { key: 'van', label: 'Van', emoji: '🚐' },
  { key: 'truck', label: 'Truck', emoji: '🛻' },
  { key: 'unmarked', label: 'Unmarked', emoji: '🚘' },
  { key: 'other', label: 'Other', emoji: '🚗' },
]

export const VEHICLE_COLORS: Array<{ key: string; label: string; hex: string }> = [
  { key: 'black', label: 'Black', hex: '#1a1a1a' },
  { key: 'white', label: 'White', hex: '#ffffff' },
  { key: 'silver', label: 'Silver', hex: '#c0c0c0' },
  { key: 'gray', label: 'Gray', hex: '#808080' },
  { key: 'blue', label: 'Blue', hex: '#2563eb' },
  { key: 'red', label: 'Red', hex: '#dc2626' },
  { key: 'green', label: 'Green', hex: '#16a34a' },
  { key: 'brown', label: 'Brown', hex: '#92400e' },
  { key: 'other', label: 'Other', hex: '#6b7280' },
]

export const VEHICLE_DIRECTIONS: Array<{ key: VehicleDirection; label: string; arrow: string }> = [
  { key: 'N', label: 'North', arrow: '↑' },
  { key: 'NE', label: 'Northeast', arrow: '↗' },
  { key: 'E', label: 'East', arrow: '→' },
  { key: 'SE', label: 'Southeast', arrow: '↘' },
  { key: 'S', label: 'South', arrow: '↓' },
  { key: 'SW', label: 'Southwest', arrow: '↙' },
  { key: 'W', label: 'West', arrow: '←' },
  { key: 'NW', label: 'Northwest', arrow: '↖' },
  { key: 'stationary', label: 'Stationary', arrow: '⊙' },
]

/**
 * Photo attachment with geo-tagging and OCR data
 */
export interface SightingPhoto {
  id: string
  thumbnailDataUrl: string          // Base64 data URL for preview
  blobKey: string                   // Key for retrieving full image from IndexedDB
  location?: GeoLocation            // From EXIF or device GPS
  timestamp?: Date                  // From EXIF or capture time
  plateOCR?: {                      // OCR-detected plate
    text: string
    confidence: number              // 0-1
  }
}

/**
 * A vehicle sighting report
 */
export interface VehicleSighting {
  id: string
  type: 'vehicle_sighting'
  location: GeoLocation
  plate?: string                    // License plate (optional, 2-10 chars)
  plateConfidence: PlateConfidence
  vehicleType?: VehicleType
  vehicleColor?: string            // Color key from VEHICLE_COLORS
  description?: string             // Free text, max 100 chars
  direction?: VehicleDirection
  isFollowing: boolean             // Patrol is actively tailing
  followerId?: string              // PeerId of following patrol
  photos?: SightingPhoto[]         // Attached photos (max 3)
  senderId: string
  senderName: string
  createdAt: Date
  expiresAt: Date                  // Auto-expire after 30 min by default
  clearedAt?: Date
  clearedBy?: string
}

// ============= Plate Database Types =============

export type PlateStatus = 'confirmed_hostile' | 'suspected' | 'cleared' | 'unknown'

export const PLATE_STATUS_CONFIG: Record<PlateStatus, { label: string; color: string; emoji: string }> = {
  confirmed_hostile: { label: 'Confirmed ICE', color: '#ef4444', emoji: '🔴' },
  suspected: { label: 'Suspected', color: '#f59e0b', emoji: '🟡' },
  cleared: { label: 'Cleared', color: '#22c55e', emoji: '🟢' },
  unknown: { label: 'Unknown', color: '#6b7280', emoji: '⚪' },
}

/**
 * A single sighting record for a plate
 */
export interface PlateSighting {
  id: string
  timestamp: Date
  location: GeoLocation
  roomId: string
  reporterId: string
  reporterName: string
  vehicleType?: VehicleType
  vehicleColor?: string
  direction?: VehicleDirection
}

/**
 * A plate record in the database
 */
export interface PlateRecord {
  plate: string                    // Normalized (uppercase, no spaces)
  status: PlateStatus
  firstSeen: Date
  lastSeen: Date
  sightingCount: number
  sightings: PlateSighting[]
  notes?: string                   // Coordinator notes
  updatedBy: string                // PeerId who last updated status
  updatedByName: string
  updatedAt: Date
}

// ============= Handoff Types =============

export type HandoffStatus = 'pending' | 'accepted' | 'expired' | 'failed'

export interface HandoffRequest {
  id: string
  vehicleSighting: VehicleSighting
  fromZone: string                 // Room ID
  fromZoneName: string
  toZones: string[]                // Adjacent room IDs
  fromPatrol: string               // PeerId
  fromPatrolName: string
  status: HandoffStatus
  acceptedBy?: string              // PeerId who accepted
  acceptedByName?: string
  acceptedAt?: Date
  createdAt: Date
  expiresAt: Date                  // 2 minutes default
}

// ============= Alert Types =============

export type AlertSoundType =
  | 'vehicle_confirmed'            // Confirmed ICE vehicle
  | 'vehicle_unknown'              // Unknown vehicle sighting
  | 'need_help'                    // Emergency help request
  | 'handoff'                      // Handoff request
  | 'boundary'                     // Zone boundary warning
  | 'whistle'                      // Mass alert
  | 'voice_message'                // New voice message

export interface AlertSettings {
  enabled: boolean
  volume: number                   // 0-100
  vibration: boolean
  radius: number                   // Alert radius in meters (100-5000)
  criticalBypassRadius: boolean    // Critical alerts bypass radius filter
}

export const DEFAULT_ALERT_SETTINGS: AlertSettings = {
  enabled: true,
  volume: 80,
  vibration: true,
  radius: 1000,
  criticalBypassRadius: true,
}

// ============= Constants =============

/** Default vehicle sighting expiration in minutes */
export const DEFAULT_VEHICLE_SIGHTING_EXPIRATION_MINUTES = 30

/** Zone boundary buffer distance in meters */
export const ZONE_BOUNDARY_BUFFER_METERS = 200

/** Handoff request expiration in minutes */
export const HANDOFF_EXPIRATION_MINUTES = 2

/** Maximum plate length */
export const MAX_PLATE_LENGTH = 10

/** Minimum plate length */
export const MIN_PLATE_LENGTH = 2

/** Maximum description length */
export const MAX_DESCRIPTION_LENGTH = 100

// ============= Utility Functions =============

/**
 * Normalize a license plate for database lookup
 * - Uppercase
 * - Remove spaces and dashes
 * - Trim whitespace
 */
export function normalizePlate(plate: string): string {
  return plate
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .trim()
}

/**
 * Validate a license plate
 */
export function isValidPlate(plate: string): boolean {
  const normalized = normalizePlate(plate)
  return normalized.length >= MIN_PLATE_LENGTH && normalized.length <= MAX_PLATE_LENGTH
}

/**
 * Get the color hex for a vehicle color key
 */
export function getVehicleColorHex(colorKey: string): string {
  const color = VEHICLE_COLORS.find(c => c.key === colorKey)
  return color?.hex || '#6b7280'
}

/**
 * Get the direction arrow for a direction key
 */
export function getDirectionArrow(direction: VehicleDirection): string {
  const dir = VEHICLE_DIRECTIONS.find(d => d.key === direction)
  return dir?.arrow || ''
}

/**
 * Check if a plate status indicates a hostile vehicle
 */
export function isHostilePlate(status: PlateStatus): boolean {
  return status === 'confirmed_hostile' || status === 'suspected'
}
